
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { userContext } from '@/contexts/user';
import { login } from '@/store/slices/authSlice';
import PlatformLogo from './PlatformLogo';

const AuthForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { login: cognitoLogin } = useContext(userContext);

  useEffect(() => {
    // Trigger logo animation immediately after component mounts
    const timer = setTimeout(() => {
      setShowAnimation(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    
    cognitoLogin(email, password, {
      onSuccess: (session) => {
        setIsLoading(false);
        
        // Update Redux store with user info
        dispatch(login({
          user: {
            id: session.idToken,
            email: email,
            name: email.split('@')[0]
          }
        }));
        
        // Check for intended route in sessionStorage
        const intendedRoute = sessionStorage.getItem('vitl_intended_route');
        const redirectTo = intendedRoute || '/dashboard';
        
        // Clear the intended route after using it
        if (intendedRoute) {
          sessionStorage.removeItem('vitl_intended_route');
          console.log('[AuthForm] Redirecting to intended route:', intendedRoute);
        }
        
        toast({
          title: "Welcome back",
          description: "You have successfully signed in to VITL.",
        });
        navigate(redirectTo);
      },
      onFailure: (err) => {
        setIsLoading(false);
        toast({
          title: "Login Failed",
          description: err.message || "Invalid email or password. Please try again.",
          variant: "destructive",
        });
      }
    });
  };

  const handle2FASubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (otpValue.length !== 6) return;
    
    setIsLoading(true);
    
    // Simulate 2FA verification
    setTimeout(() => {
      setIsLoading(false);
      
      // Check for intended route in sessionStorage
      const intendedRoute = sessionStorage.getItem('vitl_intended_route');
      const redirectTo = intendedRoute || '/dashboard';
      
      // Clear the intended route after using it
      if (intendedRoute) {
        sessionStorage.removeItem('vitl_intended_route');
        console.log('[AuthForm 2FA] Redirecting to intended route:', intendedRoute);
      }
      
      toast({
        title: "Welcome back",
        description: "You have successfully signed in to VITL.",
      });
      navigate(redirectTo);
    }, 2000);
  };

  return (
    <div className="w-full max-w-xs mx-auto">
      <div 
        className={`transition-all duration-1000 ease-out ${
          showAnimation 
            ? 'mb-8 transform translate-y-0' 
            : 'transform -translate-y-full'
        }`}
      >
        <PlatformLogo />
      </div>
      
      <Card 
        className={`bg-white/95 backdrop-blur-sm border-0 shadow-2xl transition-all duration-1000 ease-out delay-1000 ${
          showAnimation 
            ? 'opacity-100 transform translate-y-0' 
            : 'opacity-0 transform translate-y-8'
        }`}
      >
        <CardContent>
          {!show2FA ? (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-vitl-navy font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-vitl-navy font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-vitl-dark/20 focus:border-vitl-green focus:ring-vitl-green"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-vitl-green hover:bg-vitl-green/90 text-white font-semibold py-2.5"
                disabled={isLoading}
              >
                {isLoading ? "Logging In..." : "Log In"}
              </Button>
              <div className="text-center mt-4">
                <p className="text-sm text-vitl-dark/70">
                  New to VITL?{' '}
                  <Link to="/signup" className="text-vitl-green hover:text-vitl-green/90 font-medium underline">
                    Register here
                  </Link>
                </p>
              </div>
            </form>
          ) : (
            <form onSubmit={handle2FASubmit} className="space-y-4">
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold text-vitl-navy">Two-Factor Authentication</h3>
                <p className="text-sm text-vitl-dark/70">Enter the 6-digit code from your authenticator app</p>
              </div>
              <div className="space-y-2">
                <Label className="text-vitl-navy font-medium">
                  Verification Code
                </Label>
                <div className="flex justify-center">
                  <InputOTP 
                    maxLength={6} 
                    value={otpValue} 
                    onChange={setOtpValue}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-vitl-green hover:bg-vitl-green/90 text-white font-semibold py-2.5"
                disabled={isLoading || otpValue.length !== 6}
              >
                {isLoading ? "Verifying..." : "Verify Code"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthForm;
